// Engineering Validation Panel — Structured validation results display
// Professional engineering-grade UI with color-coded status indicators

import React from 'react';
import { ValidationReport, ValidationItem, ValidationStatus } from './engineeringValidator';
import { Shield, AlertTriangle, XCircle, CheckCircle2, FileWarning, ArrowLeft, ClipboardCheck } from 'lucide-react';

// ─── STATUS STYLING ──────────────────────────────────────────────────────────

function getStatusStyles(status: ValidationStatus) {
  switch (status) {
    case 'accepted':
      return {
        bg: 'bg-green-500/5',
        border: 'border-green-500/30',
        badge: 'bg-green-500/10 border-green-500/40 text-green-300',
        icon: <CheckCircle2 className="w-4 h-4 text-green-400" />,
        label: 'ACCEPTED',
        dot: 'bg-green-400',
      };
    case 'conditional':
      return {
        bg: 'bg-amber-500/5',
        border: 'border-amber-500/30',
        badge: 'bg-amber-500/10 border-amber-500/40 text-amber-300',
        icon: <AlertTriangle className="w-4 h-4 text-amber-400" />,
        label: 'CONDITIONAL',
        dot: 'bg-amber-400',
      };
    case 'rejected':
      return {
        bg: 'bg-red-500/5',
        border: 'border-red-500/30',
        badge: 'bg-red-500/10 border-red-500/40 text-red-300',
        icon: <XCircle className="w-4 h-4 text-red-400" />,
        label: 'REJECTED',
        dot: 'bg-red-400',
      };
  }
}

function getOverallStatusStyles(status: string) {
  switch (status) {
    case 'Approved':
      return {
        bg: 'bg-green-500/10',
        border: 'border-green-500/40',
        text: 'text-green-300',
        icon: <CheckCircle2 className="w-6 h-6 text-green-400" />,
        description: 'All requested changes validated — no engineering or safety constraints violated.',
      };
    case 'Conditional':
      return {
        bg: 'bg-amber-500/10',
        border: 'border-amber-500/40',
        text: 'text-amber-300',
        icon: <AlertTriangle className="w-6 h-6 text-amber-400" />,
        description: 'One or more changes carry elevated operational risk. Submission permitted with acknowledged conditions.',
      };
    case 'Rejected':
      return {
        bg: 'bg-red-500/10',
        border: 'border-red-500/40',
        text: 'text-red-300',
        icon: <XCircle className="w-6 h-6 text-red-400" />,
        description: 'One or more changes violate engineering or safety constraints. Submission blocked until resolved.',
      };
    default:
      return {
        bg: 'bg-slate-800/50',
        border: 'border-slate-700',
        text: 'text-slate-300',
        icon: <Shield className="w-6 h-6 text-slate-400" />,
        description: '',
      };
  }
}

// ─── VALIDATION ITEM CARD ────────────────────────────────────────────────────

const ValidationItemCard: React.FC<{ item: ValidationItem }> = ({ item }) => {
  const styles = getStatusStyles(item.status);

  return (
    <div className={`rounded-lg border ${styles.border} ${styles.bg} p-4 transition-all`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">{styles.icon}</div>
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <h4 className="text-white text-sm font-semibold">{item.equipment}</h4>
            <span className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${styles.badge}`}>
              {styles.label}
            </span>
          </div>

          {/* Rule reference */}
          <p className="text-slate-500 text-[11px] uppercase tracking-wide font-medium mb-2">
            Rule: {item.rule}
          </p>

          {/* Primary message */}
          <p className="text-slate-300 text-sm leading-relaxed mb-2">{item.message}</p>

          {/* Technical basis (expanded view) */}
          <div className="bg-slate-800/60 rounded p-3 border border-slate-700/50">
            <p className="text-slate-400 text-xs leading-relaxed">
              <span className="text-slate-500 font-medium uppercase text-[10px] tracking-wide">Technical Basis: </span>
              {item.technicalBasis}
            </p>
          </div>

          {/* Risk level indicator */}
          {item.riskLevel && item.riskLevel !== 'low' && (
            <div className="flex items-center gap-2 mt-2">
              <span className={`w-2 h-2 rounded-full ${
                item.riskLevel === 'critical' ? 'bg-red-400 animate-pulse' :
                item.riskLevel === 'high' ? 'bg-orange-400' : 'bg-amber-400'
              }`}></span>
              <span className="text-slate-500 text-[11px] uppercase tracking-wide font-medium">
                Risk Level: {item.riskLevel}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN VALIDATION PANEL ───────────────────────────────────────────────────

interface ValidationPanelProps {
  report: ValidationReport;
  onGoBack: () => void;
}

export const ValidationPanel: React.FC<ValidationPanelProps> = ({ report, onGoBack }) => {
  const overallStyles = getOverallStatusStyles(report.overallStatus);
  const totalItems = report.acceptedChanges.length + report.conditionalChanges.length + report.rejectedChanges.length;

  // If no changes were requested
  if (totalItems === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-green-500/10 border border-green-500/40 rounded-lg p-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
          <h3 className="font-['Oswald'] text-lg text-green-300 uppercase tracking-wide mb-2">No Modifications Detected</h3>
          <p className="text-slate-400 text-sm">
            Original engineering configuration accepted as proposed. Validation not required.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ─── OVERALL STATUS BANNER ─────────────────────────────────────── */}
      <div className={`rounded-lg border ${overallStyles.border} ${overallStyles.bg} p-5`}>
        <div className="flex items-start gap-4">
          <div className="shrink-0 mt-0.5">{overallStyles.icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className={`font-['Oswald'] text-xl uppercase tracking-wide ${overallStyles.text}`}>
                {report.overallStatus}
              </h3>
              <span className="text-slate-500 text-xs">
                {totalItems} item{totalItems !== 1 ? 's' : ''} reviewed
              </span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">{overallStyles.description}</p>
          </div>
        </div>

        {/* Summary counts */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-700/50">
          {report.acceptedChanges.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-green-400"></span>
              <span className="text-green-300 text-xs font-medium">{report.acceptedChanges.length} Accepted</span>
            </div>
          )}
          {report.conditionalChanges.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
              <span className="text-amber-300 text-xs font-medium">{report.conditionalChanges.length} Conditional</span>
            </div>
          )}
          {report.rejectedChanges.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400 animate-pulse"></span>
              <span className="text-red-300 text-xs font-medium">{report.rejectedChanges.length} Rejected</span>
            </div>
          )}
        </div>
      </div>

      {/* ─── REJECTED CHANGES (shown first if any) ─────────────────────── */}
      {report.rejectedChanges.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <XCircle className="w-4 h-4 text-red-400" />
            <h3 className="font-['Oswald'] text-sm text-red-300 uppercase tracking-wide">
              Rejected Changes ({report.rejectedChanges.length})
            </h3>
          </div>
          <div className="space-y-3">
            {report.rejectedChanges.map(item => (
              <ValidationItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* ─── CONDITIONAL CHANGES ───────────────────────────────────────── */}
      {report.conditionalChanges.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h3 className="font-['Oswald'] text-sm text-amber-300 uppercase tracking-wide">
              Conditional Changes ({report.conditionalChanges.length})
            </h3>
          </div>
          <div className="space-y-3">
            {report.conditionalChanges.map(item => (
              <ValidationItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* ─── ACCEPTED CHANGES ──────────────────────────────────────────── */}
      {report.acceptedChanges.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <h3 className="font-['Oswald'] text-sm text-green-300 uppercase tracking-wide">
              Accepted Changes ({report.acceptedChanges.length})
            </h3>
          </div>
          <div className="space-y-3">
            {report.acceptedChanges.map(item => (
              <ValidationItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* ─── ENGINEERING RECOMMENDATION ────────────────────────────────── */}
      <div className="border border-slate-700/50 rounded-lg overflow-hidden">
        <div className="bg-slate-800/60 px-4 py-3 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-orange-400" />
            <h3 className="font-['Oswald'] text-sm text-orange-300 uppercase tracking-wide">
              Engineering Recommendation
            </h3>
          </div>
        </div>
        <div className="p-4 bg-slate-800/30">
          <pre className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap font-body">
            {report.engineeringRecommendation}
          </pre>
        </div>
      </div>

      {/* ─── SUBMISSION STATUS INDICATOR ───────────────────────────────── */}
      {!report.canSubmit && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FileWarning className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 text-sm font-semibold mb-1">Submission Blocked</p>
              <p className="text-red-400/80 text-xs leading-relaxed">
                One or more changes have been rejected by the engineering validation engine. 
                Return to the Review & Adjust step to reinstate required equipment before proceeding.
              </p>
              <button
                onClick={onGoBack}
                className="mt-3 flex items-center gap-2 px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-slate-300 text-xs font-medium hover:bg-slate-600/50 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Return to Review & Adjust
              </button>
            </div>
          </div>
        </div>
      )}

      {report.canSubmit && report.hasWarnings && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 text-sm font-semibold mb-1">Submission Permitted with Conditions</p>
              <p className="text-amber-400/80 text-xs leading-relaxed">
                Conditional items carry elevated operational risk. By proceeding, client acknowledges 
                acceptance of identified risks. EnergiPro engineering team will include risk mitigation 
                measures in the final technical proposal.
              </p>
            </div>
          </div>
        </div>
      )}

      {report.canSubmit && !report.hasWarnings && (
        <div className="bg-green-500/10 border border-green-500/40 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-green-300 text-sm font-semibold mb-1">Validation Passed</p>
              <p className="text-green-400/80 text-xs leading-relaxed">
                All requested changes have been validated against engineering and safety constraints. 
                Configuration is technically sound. Submission may proceed.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
